import { useState, useEffect, useCallback } from "react";
import PubSub from "pubsub-js";
import getKPIController from "../realtime-controller";
import { ReadKPI } from "../api";
import { bucketFromNow } from "./time-buckets";

export default function useKPIData({
    kpi,
    appId,
    timeRange = "24h",
    granularity = "hour",
    aggregation = "avg",
    tagFilters = {},
    groupByTag = null,
    live = false,
}) {
    const [data, setData] = useState([]);
    const [latestTags, setLatestTags] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const effectiveAppId = appId || "";

    const fetchData = useCallback(async () => {
        if (!kpi) return;
        setLoading(true);
        try {
            const { from, to } = bucketFromNow(timeRange);
            const result = await ReadKPI({
                kpi,
                appId: effectiveAppId,
                from: from.toISOString(),
                to: to.toISOString(),
                granularity,
                aggregation,
                tagFilters,
                groupByTag,
            });
            if (result.success) {
                setData(result.buckets || []);
                setLatestTags(result.latestTags || null);
            } else {
                setError(result.error);
            }
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    }, [kpi, effectiveAppId, timeRange, granularity, aggregation, JSON.stringify(tagFilters), groupByTag]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!live || !kpi) return;

        const controller = getKPIController();
        controller.subscribe(kpi, effectiveAppId);

        const channel = `kpi:event:${kpi}:${effectiveAppId}`;
        const token = PubSub.subscribe(channel, (msg, event) => {
            if (event.tags) {
                setLatestTags((prev) => ({ ...prev, ...event.tags }));
            }
            setData((prev) => [
                ...prev.slice(-199),
                { timestamp: new Date(event.timestamp).toISOString(), value: event.value },
            ]);
        });

        return () => {
            controller.unsubscribe(kpi, effectiveAppId);
            PubSub.unsubscribe(token);
        };
    }, [live, kpi, effectiveAppId]);

    return { data, loading, error, latestTags, refresh: fetchData };
}
